import React from 'react';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import CheckIcon from '@mui/icons-material/Check';
import PlumbingIcon from '@mui/icons-material/Plumbing';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { Tooltip, Typography } from '@mui/material';
import { DeleteForever, ErrorOutline, PauseCircle } from '@mui/icons-material';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { PassOrderPageData, PassOut, PassPaymentStatus, PassStatus, PassType, RecurringBillingMode } from '../../api';

export const isRecurringPassType = (passType?: PassType): boolean =>
  passType === PassType.UNLIMITED_MONTHLY || passType === PassType.FIXED_SCHEDULE_MONTHLY;

export const isFixedScheduleMonthly = (passType?: PassType): boolean => passType === PassType.FIXED_SCHEDULE_MONTHLY;

export const supportsSchedule = (passType?: PassType): boolean =>
  passType === PassType.FIXED_SCHEDULE_MONTHLY || passType === PassType.PUNCH_BASED;

export const requiresExactWeeklyCount = (passType?: PassType): boolean => passType === PassType.FIXED_SCHEDULE_MONTHLY;

/**
 * The last renewal attempt was declined. Dunning keeps the pass active and retries on +1/+3/+5 days
 * (RENEWAL_RETRY_OFFSET_DAYS), so without this a failing membership reads as a perfectly healthy one.
 *
 * Read the array tail rather than sorting by date: failure records carry no `cycle_start`, and the
 * tail is exactly what the server's own _consecutive_renewal_failures walks back from. Test for an
 * explicit `false` -- `succeeded` defaults to true server-side, so older records read back true.
 *
 * Suspended passes are excluded on purpose: the legacy engine records a failure reading "renewal
 * received while suspended" when Grow charges a paused membership, and that is not a decline.
 */
export const isRenewalFailing = (pass: PassOut, passOrderPage?: PassOrderPageData): boolean =>
  isRecurringPassType(passOrderPage?.pass_type) &&
  pass.status === PassStatus.ACTIVE &&
  pass.payments?.[pass.payments.length - 1]?.succeeded === false;

/**
 * Dunning is still retrying, rather than having given up. After the fourth consecutive failure the
 * server clears both of these (pass_logic._handle_failed_token_renewal), so they separate a
 * membership we are still trying to bill from one that has stopped.
 */
export const isRenewalRetrying = (pass: PassOut): boolean => pass.auto_renew !== false && !!pass.next_charge_date;

export enum RecurringAction {
  Pause = 'pause',
  Resume = 'resume',
  Cancel = 'cancel',
  Reactivate = 'reactivate',
  CancelPause = 'cancelPause',
  EditPause = 'editPause',
}

export type RecurringSubscriptionView = {
  label: string;
  actions: RecurringAction[];
};

export const getRecurringSubscriptionView = (
  pass: PassOut,
  passOrderPage: PassOrderPageData | undefined,
  t: TFunction,
): RecurringSubscriptionView => {
  if (!isRecurringPassType(passOrderPage?.pass_type)) return { label: '', actions: [] };
  const date = pass.expiration_date ? moment(pass.expiration_date).format('ll') : '';
  const pauseStart = pass.pause_start_date ? moment(pass.pause_start_date).format('ll') : '';
  const pauseEnd = pass.pause_end_date ? moment(pass.pause_end_date).format('ll') : '';
  const autoRenew = pass.auto_renew;
  if (pass.status === PassStatus.EXPIRED) {
    return { label: t('passes.recurring.status.expired', { date }), actions: [] };
  }
  if (pass.status === PassStatus.ACTIVE) {
    if (isRenewalFailing(pass, passOrderPage)) {
      return isRenewalRetrying(pass)
        ? {
            label: t('passes.recurring.status.failingRetry', {
              date: pass.next_charge_date ? moment(pass.next_charge_date).format('ll') : '',
            }),
            actions: [RecurringAction.Pause, RecurringAction.Cancel],
          }
        : {
            label: t('passes.recurring.status.failingStopped', { date }),
            actions: [RecurringAction.Reactivate],
          };
    }
    if (autoRenew === false) {
      return {
        label: t('passes.recurring.status.activeCanceling', { date }),
        actions: [RecurringAction.Reactivate],
      };
    }
    if (pass.pause_start_date) {
      return {
        label: t('passes.recurring.status.pauseScheduled', { start: pauseStart, end: pauseEnd }),
        actions: [RecurringAction.EditPause, RecurringAction.CancelPause, RecurringAction.Cancel],
      };
    }
    return {
      label: t('passes.recurring.status.activeRenews', { date }),
      actions: [RecurringAction.Pause, RecurringAction.Cancel],
    };
  }
  if (pass.status === PassStatus.SUSPENDED) {
    if (pass.pause_end_date) {
      return {
        label: t('passes.recurring.status.pausedUntil', { date: pauseEnd }),
        actions: [RecurringAction.EditPause, RecurringAction.Resume, RecurringAction.Cancel],
      };
    }
    if (autoRenew === false) {
      return {
        label: t('passes.recurring.status.pausedCanceling', { date }),
        actions: [RecurringAction.Reactivate],
      };
    }
    return {
      label: t('passes.recurring.status.paused'),
      actions: [RecurringAction.Resume, RecurringAction.Cancel],
    };
  }
  return { label: '', actions: [] };
};

export enum PassSwapBlock {
  NotRecurring = 'notRecurring',
  NotActive = 'notActive',
  Paused = 'paused',
  CancellationScheduled = 'cancellationScheduled',
  NoSubscription = 'noSubscription',
  NoTargets = 'noTargets',
}

/**
 * A swap re-prices the recurring charge, so it needs whatever the pass is actually billed against.
 * Token memberships are charged by us off a stored card and never get a provider subscription, so
 * reading `provider_subscription_ref` alone declares every one of them unswappable. Mirrors the
 * server's _has_payment_method: legacy is "not token", never an equality test on grow_direct_debit,
 * because memberships predating the token switch read back null.
 */
const hasRecurringPaymentMethod = (pass: PassOut): boolean =>
  pass.recurring_billing_mode === RecurringBillingMode.TOKEN ? !!pass.has_saved_card : !!pass.provider_subscription_ref;

/**
 * Which card link the membership needs. The two endpoints are gated on opposite values of
 * `recurring_billing_mode`, so a pass is only ever eligible for one of them: token memberships hold
 * a card we own and can replace, legacy ones hold a Grow mandate and must migrate before they have
 * a card at all. Legacy is "not token" rather than an equality test -- see hasRecurringPaymentMethod.
 */
export enum CardAction {
  Replace = 'replace',
  MoveToToken = 'moveToToken',
}

export enum CardActionBlock {
  NotRecurring = 'notRecurring',
  NotActive = 'notActive',
  NoIntegration = 'noIntegration',
}

export const getCardAction = (pass: PassOut): CardAction =>
  pass.recurring_billing_mode === RecurringBillingMode.TOKEN ? CardAction.Replace : CardAction.MoveToToken;

/**
 * Mirrors PassLogic.get_card_update_link's preconditions so the action can be disabled with a reason
 * instead of failing on submit. Suspended passes are deliberately allowed through alongside active
 * ones, exactly as the server does -- a paused membership still needs a working card for when it
 * resumes, and a declined card is a common reason to have paused it in the first place.
 *
 * The capability check has no server counterpart because it cannot fail there: both links are provider
 * card pages, so an account whose provider cannot save cards has nothing to build one from.
 */
export const getCardActionBlock = (
  pass: PassOut,
  passOrderPage: PassOrderPageData | undefined,
  supportsSavedCards: boolean,
): CardActionBlock | null => {
  if (!isRecurringPassType(passOrderPage?.pass_type)) return CardActionBlock.NotRecurring;
  if (!supportsSavedCards) return CardActionBlock.NoIntegration;
  if (pass.status !== PassStatus.ACTIVE && pass.status !== PassStatus.SUSPENDED) return CardActionBlock.NotActive;
  return null;
};

/**
 * Mirrors the server-side preconditions of POST /api/pass/{id}/swap so the action can be disabled
 * with a reason instead of failing on submit.
 */
export const getPassSwapBlock = (
  pass: PassOut,
  passOrderPage: PassOrderPageData | undefined,
  eligibleTargetsCount: number,
): PassSwapBlock | null => {
  if (!isRecurringPassType(passOrderPage?.pass_type)) return PassSwapBlock.NotRecurring;
  if (pass.status === PassStatus.SUSPENDED || pass.pause_start_date) return PassSwapBlock.Paused;
  if (pass.status !== PassStatus.ACTIVE) return PassSwapBlock.NotActive;
  if (pass.auto_renew === false) return PassSwapBlock.CancellationScheduled;
  if (!hasRecurringPaymentMethod(pass)) return PassSwapBlock.NoSubscription;
  if (eligibleTargetsCount === 0) return PassSwapBlock.NoTargets;
  return null;
};

export const isNotStartedYet = (pass: PassOut): boolean =>
  !!pass.start_date && moment(pass.start_date).isAfter(moment(), 'day');

export const getPassStatusIcon = (pass: PassOut, t: any, passOrderPage?: PassOrderPageData) => {
  if (pass.status === PassStatus.ACTIVE && isNotStartedYet(pass)) {
    return (
      <Tooltip title={t('passes.status.scheduled', { date: moment(pass.start_date).format('ll') })} arrow>
        <ScheduleIcon sx={{ color: 'text.secondary' }} />
      </Tooltip>
    );
  }
  if (isRenewalFailing(pass, passOrderPage)) {
    return (
      <Tooltip title={getRecurringSubscriptionView(pass, passOrderPage, t).label} arrow>
        <ErrorOutline sx={{ color: 'error.main' }} />
      </Tooltip>
    );
  }
  // A live membership isn't "counting down" like a punch pass, so it gets a check rather than an hourglass.
  if (pass.status === PassStatus.ACTIVE && isRecurringPassType(passOrderPage?.pass_type)) {
    return (
      <Tooltip title={getRecurringSubscriptionView(pass, passOrderPage, t).label || t('passes.status.active')} arrow>
        <CheckIcon sx={{ color: 'primary.main' }} />
      </Tooltip>
    );
  }
  switch (pass.status) {
    case PassStatus.ACTIVE:
      return (
        <Tooltip title={t('passes.status.active')} arrow>
          <HourglassEmptyIcon sx={{ color: 'text.secondary' }} />
        </Tooltip>
      );
    case PassStatus.PENDING_PAYMENT:
      return (
        <Tooltip title={t('passes.status.pending_payment')} arrow>
          <PauseCircle sx={{ color: 'text.secondary' }} />
        </Tooltip>
      );
    case PassStatus.ARCHIVED:
      return (
        <Tooltip title={t('passes.status.archived')} arrow>
          <DeleteForever sx={{ color: 'text.secondary' }} />
        </Tooltip>
      );
    case PassStatus.EXPIRED:
      return (
        <Tooltip title={t('passes.status.expired')} arrow>
          <HourglassBottomIcon sx={{ color: 'text.secondary' }} />
        </Tooltip>
      );
    default:
      return null;
  }
};

export const getPassPaymentStatusIcon = (status: PassPaymentStatus, t: any) => {
  switch (status) {
    case PassPaymentStatus.PENDING_PAYMENT:
      return t('passes.payment_status.pending_payment');
    case PassPaymentStatus.PAID:
      return (
        <Tooltip title={t('passes.payment_status.paid')} arrow>
          <AutoAwesomeIcon sx={{ color: 'primary.main' }} />
        </Tooltip>
      );
    case PassPaymentStatus.MANUAL:
      return (
        <Tooltip title={t('passes.payment_status.manual')} arrow>
          <PlumbingIcon sx={{ color: 'text.secondary' }} />
        </Tooltip>
      );
    case PassPaymentStatus.CONFIRMED:
      return t('passes.payment_status.confirmed');
    default:
      return '';
  }
};

export const getExpirationDate = (pass: PassOut, passOrderPage?: PassOrderPageData): string | null => {
  if (pass.expiration_date) return pass.expiration_date;
  if (passOrderPage?.validity_days && pass.created_at) {
    return moment(pass.created_at).add(passOrderPage.validity_days, 'days').toISOString();
  }
  return null;
};

export const getDaysLeft = (pass: PassOut, passOrderPage?: PassOrderPageData): number | null => {
  const expirationDate = getExpirationDate(pass, passOrderPage);
  if (!expirationDate) {
    return null;
  }
  return moment(expirationDate).diff(moment(), 'days');
};

export const getPunchesDisplay = (
  punchesUsed: string[] | undefined,
  passOrderPage?: PassOrderPageData,
  numPunchesOverride?: number,
): string => {
  if (isFixedScheduleMonthly(passOrderPage?.pass_type)) return '—';
  const used = punchesUsed?.length || 0;
  const total = numPunchesOverride ?? (passOrderPage?.num_punches != null ? passOrderPage.num_punches : '∞');
  return `${used}/${total}`;
};

export const GuestVisitsLine = ({ pass, passOrderPage }: { pass: PassOut; passOrderPage?: PassOrderPageData }) => {
  const { t } = useTranslation();
  const quota = passOrderPage?.guest_pass_quota;
  if (!quota || pass.guest_pass_remaining == null) return null;
  const used = Math.max(0, quota - pass.guest_pass_remaining);
  const isRecurring = isRecurringPassType(passOrderPage?.pass_type);
  const suffix = isRecurring ? t('passes.used_this_cycle') : t('passes.used');
  return (
    <Typography variant="body2">
      {t('passes.guest_visits')}: {`${used}/${quota}`} {suffix}
    </Typography>
  );
};

// Signed number for punch deltas: +2, −1 (U+2212), 0.
export const formatSignedNumber = (value: number): string => {
  if (value > 0) return `+${value}`;
  if (value < 0) return `−${Math.abs(value)}`;
  return '0';
};
